import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import filter from 'lodash/filter';
import get from 'lodash/get';
import noop from 'lodash/noop';
import some from 'lodash/some';
import styled from 'styled-components';

import {
  ADDRESSES_TAB_KEY,
  DOMAINS_COLUMN_CONFIG,
  DOMAINS_TAB_KEY,
  EMAIL_ADDRESSES_COLUMN_CONFIG,
  EmailSetupModal,
  SEARCH_MAP,
} from 'src/presentation/components/pages/email/constants/emailSetup';

import AddEmailAddressModal from 'src/presentation/components/pages/email/components/AddEmailAddressModal';
import AddEmailDomainModal from 'src/presentation/components/pages/email/components/AddEmailDomainModal';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import DnsRecordsModal from 'src/presentation/components/pages/email/components/DnsRecordsModal';
import { EmailAddress } from 'src/models/Email/EmailAddress';
import EmailAddressEditor from './EmailAddressEditor';
import EmailAddressEmptyPage from './EmailAddressEmptyPage';
import emailAddressesResource from 'src/actions/email/emailAddressesResource';
import EmailAddressRowActions from './EmailAddressRowActions';
import EmailDomain from 'src/models/Email/EmailDomain';
import EmailDomainRowActions from 'src/presentation/components/pages/email/EmailDomainRowActions';
import EmailSetupDescription from 'src/presentation/components/pages/email/components/EmailSetupDescription';
import EmailSetupEmptyPage from 'src/presentation/components/pages/email/EmailSetupEmptyPage';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import { useEmailsDomainsAddresses } from 'src/presentation/components/pages/email/hooks/useEmailsDomainsAddresses';
import ViewForwardingEmailAddressModal from './components/ViewForwardingEmailAddressModal';

const EmptyFilterState = ({ itemsName, searchValue }) => (
  <>
    <H3>{`No ${itemsName} containing "${searchValue}"`}</H3>
    <p>No results found. Rephrase your search and try again.</p>
  </>
);

const renderModal = ({ activeModal, emailAddress, emailDomainItem, errors, handleCloseModal, modalActiveStep }) => {
  const modals = {
    [EmailSetupModal.NONE]: noop,
    [EmailSetupModal.DNS_RECORDS]: () => <DnsRecordsModal item={emailDomainItem} handleCloseModal={handleCloseModal} />,
    [EmailSetupModal.ADD_DOMAIN_WIZARD]: () => (
      <AddEmailDomainModal
        emailDomain={emailDomainItem}
        errors={errors.emailDomains}
        handleCloseModal={handleCloseModal}
        step={modalActiveStep}
      />
    ),
    [EmailSetupModal.ADD_EMAIL_ADDRESS_WIZARD]: () => (
      <AddEmailAddressModal
        emailAddress={emailAddress}
        errors={errors.emailAddresses}
        handleCloseModal={handleCloseModal}
        step={modalActiveStep}
      />
    ),
    [EmailSetupModal.VIEW_FORWARDING_EMAIL_ADDRESS]: () => (
      <ViewForwardingEmailAddressModal emailAddress={emailAddress} handleCloseModal={handleCloseModal} />
    ),
  };

  return modals[activeModal]();
};

const EmailSetup: TablePageRender = ({ root }) => {
  const dispatch = useDispatch();

  const renderEmailDomainsRowActions = ({ item }) => {
    return <EmailDomainRowActions item={item} openEmbedModal={handleOpenDnsRecordsModal} />;
  };

  const renderEmailAddressesRowActions = ({ item }) => {
    const [emailLocalPart, emailDomain] = item.address.split('@');
    const value: EmailAddress = {
      ...item,
      emailLocalPart,
      emailDomain,
    };
    return (
      <EmailAddressRowActions
        item={value}
        onEditSenderName={handleEditSenderName}
        onViewForwardingEmailAddress={handleViewForwardingEmailAddress}
        root={root}
      />
    );
  };

  const TAB_CONFIG = [
    {
      columns: DOMAINS_COLUMN_CONFIG,
      itemsKey: 'emailDomains',
      itemsName: 'domains',
      key: DOMAINS_TAB_KEY,
      label: 'Domains',
      renderRowActions: renderEmailDomainsRowActions,
      searchFunc: searchFunc(DOMAINS_TAB_KEY),
      showContentIfNoItems: true,
    },
    {
      columns: EMAIL_ADDRESSES_COLUMN_CONFIG,
      editor: EmailAddressEditor,
      itemsKey: 'emailAddresses',
      itemsName: 'email addresses',
      key: ADDRESSES_TAB_KEY,
      label: 'Email Addresses',
      renderRowActions: renderEmailAddressesRowActions,
      searchFunc: searchFunc(ADDRESSES_TAB_KEY),
      showContentIfNoItems: true,
    },
  ];

  const [currentTabConfig, setCurrentTabConfig] = useState(TAB_CONFIG[root === '/email-addresses' ? 1 : 0]);
  const [searchValue, setSearchValue] = useState('');

  const [activeModal, setActiveModal] = useState<EmailSetupModal>(EmailSetupModal.NONE);

  const [modalActiveStep, setModalActiveStep] = useState(1);
  const [emailDomainItem, setEmailDomainItem] = useState<EmailDomain | null>(null);
  const [emailAddress, setEmailAddress] = useState<EmailAddress | null>(null);

  const { errors, items, loadingState, resetErrors } = useEmailsDomainsAddresses();

  const handleOpenDnsRecordsModal = item => {
    setEmailDomainItem(item);
    setActiveModal(EmailSetupModal.DNS_RECORDS);
  };

  const handleCloseModal = () => {
    setActiveModal(EmailSetupModal.NONE);
    setEmailAddress(null);
    setModalActiveStep(1);
    dispatch(emailAddressesResource.actions.resetErrors({}));
  };

  const handleOpenEmailDomainModalOnSecondStep = (value: EmailDomain) => {
    setActiveModal(EmailSetupModal.ADD_DOMAIN_WIZARD);
    setModalActiveStep(2);
    setEmailDomainItem(value);
  };

  const handleOpenEmailDomainModal = () => {
    setActiveModal(EmailSetupModal.ADD_DOMAIN_WIZARD);
    setModalActiveStep(1);
  };

  const handleCreateNewEmail = () => {
    setActiveModal(EmailSetupModal.ADD_EMAIL_ADDRESS_WIZARD);
    setModalActiveStep(1);
  };

  const handleEditSenderName = (value: EmailAddress) => {
    setActiveModal(EmailSetupModal.EDIT_EMAIL_ADDRESS_WIZARD);
    setEmailAddress(value);
  };

  const handleViewForwardingEmailAddress = (value: EmailAddress) => {
    setActiveModal(EmailSetupModal.VIEW_FORWARDING_EMAIL_ADDRESS);
    setEmailAddress(value);
  };

  const handleOpenNewEmailModalOnStep2 = (value: EmailAddress) => {
    setActiveModal(EmailSetupModal.ADD_EMAIL_ADDRESS_WIZARD);
    setEmailAddress(value);
    setModalActiveStep(2);
  };

  function searchFunc(tabKey) {
    return (items, searchString) => {
      if (searchString) {
        setSearchValue(searchString);
        const search = searchString.toLowerCase().trim();
        const searchPaths = SEARCH_MAP[tabKey];
        return filter(items, item =>
          some(searchPaths, columnKey => get(item, columnKey, '').toLowerCase().includes(search))
        );
      }
      return items;
    };
  }

  const actionButton = (
    <>
      <PrimaryButton
        data-aid="action-button-domain"
        onClick={handleOpenEmailDomainModal}
        buttonType={ButtonTypes.SECONDARY}
      >
        Add domain
      </PrimaryButton>

      <Button
        data-aid="action-button-email"
        onClick={handleCreateNewEmail}
        buttonType={ButtonTypes.SECONDARY}
        disabled={!items.emailDomains.length}
      >
        Add email address
      </Button>
    </>
  );

  return (
    <>
      {loadingState !== 'succeeded' ? (
        <Spinner />
      ) : items.emailDomains.length === 0 ? (
        <EmailSetupEmptyPage errors={errors.emailDomains} onCreate={handleOpenEmailDomainModalOnSecondStep} />
      ) : (
        <TablePage
          actions={actionButton}
          data-aid="admin-email-setup-page"
          description={<EmailSetupDescription />}
          emptyFilterState={<EmptyFilterState itemsName={currentTabConfig.itemsName} searchValue={searchValue} />}
          emptyStateHideTableHeader
          emptyStateRender={
            <EmailAddressEmptyPage errors={errors.emailAddresses} onCreate={handleOpenNewEmailModalOnStep2} />
          }
          errors={errors}
          isLoading={isLoadingStatus(loadingState)}
          items={items}
          name="Email"
          onTabChange={setCurrentTabConfig}
          resetErrors={resetErrors}
          root={root}
          tabConfig={TAB_CONFIG}
          editor={[TAB_CONFIG[1]]}
        />
      )}
      {renderModal({
        activeModal,
        emailAddress,
        emailDomainItem,
        errors,
        handleCloseModal,
        modalActiveStep,
      })}
    </>
  );
};

const PrimaryButton = styled(Button)`
  margin-right: 16px;
`;

export default EmailSetup;
